import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Package, CheckCircle2, Circle, Clock, Truck, Wrench, Download,
  AlertTriangle, FileText, Send, MessageCircle, ChevronDown,
  Home, ClipboardList, Calendar, Check, X, CalendarDays,
  CheckCheck, Paperclip, Image as ImageIcon,
  Star, AlertCircle, ThumbsUp, Camera, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';
import {
  usePubliekDossier, usePubliekBerichten, useSendPubliekBericht,
  usePubliekAfspraken, useBevestigAfspraak, useWeigerAfspraak,
  useMarkeerGelezen, usePortaalBranding, useSubmitFeedback, useSubmitSchade,
  usePubliekActiviteiten,
  type DossierPubliek, type BerichtPubliek, type AfspraakPubliek, type BrandingPubliek, type ActiviteitPubliek,
} from '../hooks/useKlantportaal.js';

// ─── Helpers ───────────────────────────────────────────────

function parseNaam(json: string): string {
  try {
    const parsed = JSON.parse(json);
    return parsed.nl || parsed.fr || parsed.en || json;
  } catch {
    return json;
  }
}

function formatDatum(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDatumKort(dateStr: string): string {
  const d = new Date(dateStr);
  const vandaag = new Date();
  const tijd = d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === vandaag.toDateString()) return tijd;
  const gisteren = new Date(vandaag);
  gisteren.setDate(gisteren.getDate() - 1);
  if (d.toDateString() === gisteren.toDateString()) return `Gisteren ${tijd}`;
  return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' }) + ' ' + tijd;
}

function formatDag(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' });
}

const apparaatStatusLabel: Record<string, string> = {
  VERWACHT: 'Verwacht', BESTELD: 'Besteld', ONDERWEG: 'Onderweg',
  BINNEN: 'Binnen', GELEVERD: 'Geleverd', DEFECT: 'Defect',
};

const apparaatStatusKleur: Record<string, string> = {
  VERWACHT: 'bg-gray-100 text-gray-600',
  BESTELD: 'bg-blue-100 text-blue-700',
  ONDERWEG: 'bg-amber-100 text-amber-700',
  BINNEN: 'bg-green-100 text-green-700',
  GELEVERD: 'bg-emerald-100 text-emerald-700',
  DEFECT: 'bg-red-100 text-red-700',
};

const apparaatStatusVolgorde: Record<string, number> = {
  VERWACHT: 0, BESTELD: 1, ONDERWEG: 2, BINNEN: 3, GELEVERD: 4, DEFECT: -1,
};

const docTypeLabel: Record<string, string> = {
  keukenplan: 'Keukenplan',
  opmetingsverslag: 'Opmetingsverslag',
  opleveringsrapport: 'Opleveringsrapport',
  bestelbon: 'Bestelbon',
  offerte: 'Offerte',
  plaatsingsdossier: 'Plaatsingsdossier',
  bon_voor_levering: 'Bon voor levering',
};

const GOLD = '#C8A961';

// ─── Sub Components ────────────────────────────────────────

function StapIcoon({ status }: { status: string }) {
  if (status === 'completed') return <CheckCircle2 size={20} className="text-green-500" />;
  if (status === 'active') return <Clock size={20} className="text-brand" />;
  return <Circle size={16} className="text-gray-300" />;
}

function TrajectTimeline({ stappen }: { stappen: DossierPubliek['trajectstappen'] }) {
  if (stappen.length === 0) return null;

  const voltooid = stappen.filter(s => s.status === 'completed').length;
  const percentage = Math.round((voltooid / stappen.length) * 100);

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Voortgang project</h2>
        <span className="text-sm font-medium text-brand">{percentage}%</span>
      </div>

      <div className="h-2.5 bg-gray-100 rounded-full mb-6 overflow-hidden">
        <div
          className="h-full bg-brand rounded-full transition-all duration-700"
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="space-y-0">
        {stappen.map((stap, i) => {
          const isLast = i === stappen.length - 1;
          return (
            <div key={stap.id} className="flex gap-3 sm:gap-4">
              <div className="flex flex-col items-center">
                <StapIcoon status={stap.status} />
                {!isLast && (
                  <div className={`w-0.5 flex-1 my-1 ${stap.status === 'completed' ? 'bg-green-200' : 'bg-gray-200'}`} />
                )}
              </div>

              <div className={`pb-5 flex-1 ${isLast ? 'pb-0' : ''}`}>
                <div className="flex items-center gap-2">
                                    <span className={`text-sm font-medium ${
                    stap.status === 'completed' ? 'text-gray-500 line-through' :
                    stap.status === 'active' ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {parseNaam(stap.template.naam)}
                  </span>
                  {stap.status === 'active' && (
                    <span className="text-[10px] font-medium bg-brand/10 text-brand px-2 py-0.5 rounded-full">Actief</span>
                  )}
                </div>
                {stap.geplandeDatum && stap.status !== 'completed' && (
                  <p className={`text-xs mt-1 ml-7 ${stap.status === 'active' ? 'text-brand' : 'text-gray-400'}`}>
                    Gepland: {formatDatum(stap.geplandeDatum)}
                  </p>
                )}
                {stap.status === 'completed' && stap.afgerondDatum && (
                  <p className="text-xs text-gray-400 mt-1 ml-7">Afgerond: {formatDatum(stap.afgerondDatum)}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ApparatenLijst({ apparaten }: { apparaten: DossierPubliek['apparaten'] }) {
  if (apparaten.length === 0) return null;

  const geleverd = apparaten.filter(a => a.status === 'GELEVERD' || a.status === 'BINNEN').length;
  const gesorteerd = [...apparaten].sort((a, b) =>
    (apparaatStatusVolgorde[a.status] ?? 0) - (apparaatStatusVolgorde[b.status] ?? 0)
  );

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Package size={18} className="text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Apparaten</h2>
        </div>
        <span className="text-xs text-gray-400">{geleverd}/{apparaten.length} geleverd</span>
      </div>

      <div className="space-y-2">
        {gesorteerd.map(app => (
          <div key={app.id} className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition-colors">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{app.naam}</p>
              <div className="flex items-center gap-2 mt-0.5">
                {app.merk && <p className="text-xs text-gray-400">{app.merk}</p>}
                {app.binnenDatum && <p className="text-xs text-gray-400">Binnen: {formatDatum(app.binnenDatum)}</p>}
              </div>
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ml-2 ${apparaatStatusKleur[app.status] || 'bg-gray-100 text-gray-600'}`}>
              {apparaatStatusLabel[app.status] || app.status}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function DocumentenLijst({ documenten, token }: { documenten: DossierPubliek['documenten']; token: string }) {
  if (documenten.length === 0) return null;

  const handleDownload = (docId: string, naam: string) => {
    const link = document.createElement('a');
    link.href = `/api/klantportaal/${token}/documenten/${docId}/download`;
    link.download = naam;
    link.target = '_blank';
    link.click();
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <FileText size={18} className="text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Documenten</h2>
        <span className="text-xs text-gray-400 ml-auto">{documenten.length}</span>
      </div>

      <div className="space-y-2">
        {documenten.map(doc => (
          <div
            key={doc.id}
            className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition-colors group"
          >
            <div className="w-9 h-9 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0">
              <FileText size={16} className="text-brand" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{doc.naam}</p>
              <p className="text-xs text-gray-400">
                {doc.documentType ? (docTypeLabel[doc.documentType] || doc.documentType) + ' · ' : ''}
                {formatDatum(doc.createdAt)}
              </p>
            </div>
            <button
              onClick={() => handleDownload(doc.id, doc.naam)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 hover:text-brand hover:bg-brand/10 transition-all opacity-0 group-hover:opacity-100 sm:opacity-100"
              title="Downloaden"
            >
              <Download size={16} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function DatumKaarten({ dossier }: { dossier: DossierPubliek }) {
  const datums = [
    dossier.leveringsdatum && {
      label: 'Verwachte levering',
      datum: dossier.leveringsdatum,
      icon: <Truck size={16} className="text-brand" />,
    },
    dossier.plaatsingsdatumStart && {
      label: 'Plaatsing',
      datum: dossier.plaatsingsdatumStart,
      eindDatum: dossier.plaatsingsdatumEind,
      icon: <Wrench size={16} className="text-brand" />,
    },
  ].filter(Boolean) as Array<{ label: string; datum: string; eindDatum?: string | null; icon: React.ReactNode }>;

  if (datums.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {datums.map(d => (
        <div key={d.label} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center flex-shrink-0">
            {d.icon}
          </div>
          <div>
            <p className="text-xs text-gray-400">{d.label}</p>
            <p className="text-sm font-semibold text-gray-900">
              {formatDatum(d.datum)}
              {d.eindDatum && d.eindDatum !== d.datum && (
                <span className="font-normal text-gray-400"> – {formatDatum(d.eindDatum)}</span>
              )}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Afspraken Component ───────────────────────────────────

function AfsprakenLijst({ token }: { token: string }) {
  const { data: afspraken = [], isLoading } = usePubliekAfspraken(token);
  const bevestig = useBevestigAfspraak(token);
  const weiger = useWeigerAfspraak(token);
  const [selectedDatum, setSelectedDatum] = useState<Record<string, string>>({});

  if (isLoading) {
    return (
      <section className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (afspraken.length === 0) {
    return (
      <section className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays size={18} className="text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Afspraken</h2>
        </div>
        <div className="text-center py-8">
          <Calendar size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Nog geen afspraken gepland.</p>
        </div>
      </section>
    );
  }

  const statusConfig: Record<string, { label: string; kleur: string; icon: React.ReactNode }> = {
    voorstel: { label: 'Wacht op bevestiging', kleur: 'bg-amber-100 text-amber-700', icon: <Clock size={14} /> },
    bevestigd: { label: 'Bevestigd', kleur: 'bg-green-100 text-green-700', icon: <Check size={14} /> },
    geweigerd: { label: 'Geweigerd', kleur: 'bg-red-100 text-red-700', icon: <X size={14} /> },
    verlopen: { label: 'Verlopen', kleur: 'bg-gray-100 text-gray-500', icon: <Clock size={14} /> },
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <CalendarDays size={18} className="text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Afspraken</h2>
        <span className="text-xs text-gray-400 ml-auto">{afspraken.length}</span>
      </div>

      <div className="space-y-4">
        {(afspraken as AfspraakPubliek[]).map(afspraak => {
          const cfg = statusConfig[afspraak.status] || statusConfig.voorstel;
          const isVoorstel = afspraak.status === 'voorstel';
          const selected = selectedDatum[afspraak.id];

          return (
            <div key={afspraak.id} className={clsx(
              'rounded-xl border p-4 transition-colors',
              isVoorstel ? 'border-brand/30 bg-brand/5' : 'border-gray-200 bg-gray-50/50',
            )}>
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{afspraak.titel}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {parseNaam(afspraak.trajectstap.template.naam)} · {afspraak.aanmaker.firstName} {afspraak.aanmaker.lastName}
                  </p>
                </div>
                <span className={clsx('flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap', cfg.kleur)}>
                  {cfg.icon} {cfg.label}
                </span>
              </div>

              {afspraak.beschrijving && (
                <p className="text-xs text-gray-500 mb-3">{afspraak.beschrijving}</p>
              )}

              {/* Bevestigde datum */}
              {afspraak.status === 'bevestigd' && afspraak.bevestigdeDatum && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
                  <Check size={16} className="text-green-600" />
                  <p className="text-sm font-medium text-green-700">
                    {formatDag(afspraak.bevestigdeDatum)}
                  </p>
                </div>
              )}

              {/* Datumvoorstellen (alleen bij voorstel status) */}
              {isVoorstel && (
                <>
                  <p className="text-xs font-medium text-gray-500 mb-2">Kies een datum die u het best past:</p>
                  <div className="space-y-2 mb-3">
                    {afspraak.datums.map(d => {
                      const isSelected = selected === d.id;
                      return (
                        <button
                          key={d.id}
                          onClick={() => setSelectedDatum(prev => ({ ...prev, [afspraak.id]: d.id }))}
                          className={clsx(
                            'w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all',
                            isSelected
                              ? 'border-brand bg-brand/10 ring-1 ring-brand/30'
                              : 'border-gray-200 bg-white hover:border-brand/50',
                          )}
                        >
                          <div className={clsx(
                            'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                            isSelected ? 'border-brand bg-brand' : 'border-gray-300',
                          )}>
                            {isSelected && <Check size={12} className="text-white" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{formatDag(d.datum)}</p>
                            {(d.vanUur || d.totUur) && (
                              <p className="text-xs text-gray-400">
                                {d.vanUur}{d.totUur ? ` – ${d.totUur}` : ''}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Actieknoppen */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (!selected) return;
                        bevestig.mutate({ afspraakId: afspraak.id, datumId: selected });
                      }}
                      disabled={!selected || bevestig.isPending}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-brand text-white rounded-xl text-sm font-semibold hover:bg-[#b89a52] active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Check size={16} /> Bevestigen
                    </button>
                    <button
                      onClick={() => weiger.mutate(afspraak.id)}
                      disabled={weiger.isPending}
                      className="px-4 py-2.5 bg-white text-gray-500 border border-gray-200 rounded-xl text-sm font-medium hover:border-red-300 hover:text-red-500 active:scale-[0.98] transition-all disabled:opacity-40"
                    >
                      Niet mogelijk
                    </button>
                  </div>

                  {(bevestig.isError || weiger.isError) && (
                    <p className="text-xs text-red-500 mt-2">Er ging iets mis. Probeer het opnieuw.</p>
                  )}
                </>
              )}

              {/* Geweigerde datums tonen */}
              {afspraak.status === 'geweigerd' && (
                <p className="text-xs text-gray-400 mt-1">
                  U heeft aangegeven dat geen van de voorgestelde datums past. Uw contactpersoon zal nieuwe datums voorstellen.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Chat Component (per fase) ────────────────────────────

interface FaseTab {
  id: string | undefined; // undefined = alle berichten
  label: string;
  icon: string;
}

function KlantChat({ token, klantNaam, fases }: {
  token: string;
  klantNaam: string;
  fases: Array<{ id: string; naam: string; icon: string }>;
}) {
  const [activeFase, setActiveFase] = useState<string | undefined>(undefined);
  const { data: berichten = [], isLoading } = usePubliekBerichten(token, activeFase);
  const sendMutation = useSendPubliekBericht(token);
  const markeerGelezen = useMarkeerGelezen(token);
  const [bericht, setBericht] = useState('');
  const [selectedFotos, setSelectedFotos] = useState<File[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const faseTabs: FaseTab[] = [
    { id: undefined, label: 'Alles', icon: '' },
    ...fases.map(f => ({ id: f.id, label: f.naam, icon: f.icon })),
  ];

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [berichten, autoScroll]);

  // Mark unread staff messages as read
  useEffect(() => {
    for (const b of berichten) {
      if (b.senderType !== 'customer' && !b.readAt) {
        markeerGelezen.mutate(b.id);
      }
    }
  }, [berichten]);

  // Simulate typing indicator when staff is active (based on recent messages)
  useEffect(() => {
    if (berichten.length === 0) return;
    const lastMsg = berichten[berichten.length - 1];
    if (lastMsg.senderType !== 'customer') {
      const age = Date.now() - new Date(lastMsg.createdAt).getTime();
      if (age < 5000) {
        setIsTyping(false);
      }
    }
  }, [berichten]);

  function handleScroll() {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 60);
  }

  function handleInputChange(value: string) {
    setBericht(value);
    // Show typing indicator briefly after typing stops (placeholder for socket.io)
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      // Would emit socket.io typing event here
    }, 500);
  }

  function handleSend() {
    const tekst = bericht.trim();
    if (!tekst && selectedFotos.length === 0) return;
    if (sendMutation.isPending) return;
    sendMutation.mutate(
      { content: tekst || '', naam: klantNaam, fase: activeFase, fotos: selectedFotos.length > 0 ? selectedFotos : undefined },
      { onSuccess: () => { setSelectedFotos([]); } },
    );
    setBericht('');
  }

  function handleFotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    setSelectedFotos(prev => [...prev, ...files].slice(0, 5));
    e.target.value = '';
  }

  function senderLabel(b: BerichtPubliek): string {
    if (b.senderType === 'customer') return 'U';
    if (b.sender) return `${b.sender.firstName} ${b.sender.lastName}`;
    if (b.senderType === 'system') return 'Systeem';
    return 'Medewerker';
  }

  function isImage(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  return (
    <section className="bg-white rounded-2xl border border-gray-200 flex flex-col" style={{ height: 'min(500px, 60vh)' }}>
      {/* Header met fase tabs */}
      <div className="border-b border-gray-100">
        <div className="px-5 py-3 flex items-center gap-2">
          <MessageCircle size={18} className="text-brand" />
          <h2 className="text-lg font-semibold text-gray-900">Berichten</h2>
          {berichten.length > 0 && (
            <span className="text-xs text-gray-400 ml-auto">{berichten.length}</span>
          )}
        </div>
        {fases.length > 0 && (
          <div className="px-3 pb-2 flex gap-1 overflow-x-auto scrollbar-hide">
            {faseTabs.map(tab => (
              <button
                key={tab.id || 'all'}
                onClick={() => setActiveFase(tab.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0',
                  activeFase === tab.id
                    ? 'bg-brand text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                )}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Berichten */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          </div>
        ) : berichten.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <MessageCircle size={32} className="text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">Nog geen berichten{activeFase ? ' in deze fase' : ''}.</p>
            <p className="text-xs text-gray-300 mt-1">Stel een vraag aan uw contactpersoon.</p>
          </div>
        ) : (
          <>
            {berichten.map(b => {
              const isKlant = b.senderType === 'customer';
              return (
                <div key={b.id} className={`flex ${isKlant ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] sm:max-w-[70%] ${
                    isKlant
                      ? 'bg-brand text-white rounded-2xl rounded-br-md'
                      : 'bg-gray-100 text-gray-900 rounded-2xl rounded-bl-md'
                  } px-4 py-2.5`}>
                    {!isKlant && (
                      <p className="text-[10px] font-medium text-brand mb-0.5">{senderLabel(b)}</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words">{b.content}</p>

                    {/* Bijlage thumbnails */}
                    {b.attachments.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {b.attachments.map(att => (
                          <div key={att.id} className={clsx(
                            'flex items-center gap-2 rounded-lg p-2 text-xs',
                            isKlant ? 'bg-white/20' : 'bg-white border border-gray-200',
                          )}>
                            {isImage(att.mimeType) ? (
                              <ImageIcon size={14} className={isKlant ? 'text-white/70' : 'text-gray-400'} />
                            ) : (
                              <Paperclip size={14} className={isKlant ? 'text-white/70' : 'text-gray-400'} />
                            )}
                            <span className={clsx('truncate', isKlant ? 'text-white/80' : 'text-gray-600')}>
                              {att.filename}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className={`flex items-center gap-1 mt-1 ${isKlant ? 'justify-end' : ''}`}>
                      <p className={`text-[10px] ${isKlant ? 'text-white/60' : 'text-gray-400'}`}>
                        {formatDatumKort(b.createdAt)}
                      </p>
                      {/* Geen leesbevestiging voor klant — asymmetrisch by design */}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-100">
        <div className="flex gap-2">
          <input
            type="text"
            value={bericht}
            onChange={e => handleInputChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
            placeholder={activeFase ? 'Schrijf een bericht over deze fase...' : 'Schrijf een bericht...'}
            maxLength={2000}
            className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-all"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFotoSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-all active:scale-95 flex-shrink-0"
            title="Foto toevoegen"
          >
            <ImageIcon size={16} />
          </button>
          <button
            onClick={handleSend}
            disabled={(!bericht.trim() && selectedFotos.length === 0) || sendMutation.isPending}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand text-white hover:bg-[#b89a52] disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 flex-shrink-0"
          >
            <Send size={16} />
          </button>
        </div>
        {/* Foto preview */}
        {selectedFotos.length > 0 && (
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
            {selectedFotos.map((f, i) => (
              <div key={i} className="relative flex-shrink-0">
                <img
                  src={URL.createObjectURL(f)}
                  alt={f.name}
                  className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                />
                <button
                  onClick={() => setSelectedFotos(prev => prev.filter((_, j) => j !== i))}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        {sendMutation.isError && (
          <p className="text-xs text-red-500 mt-1">Versturen mislukt. Probeer opnieuw.</p>
        )}
      </div>
    </section>
  );
}

// ─── VertragingBanner ─────────────────────────────────────

function VertragingBanner({ leveringsdatum }: { leveringsdatum: string }) {
  return (
    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
      <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-amber-800">
        <span className="font-semibold">Verwachte leveringsdatum {formatDatum(leveringsdatum)} is verstreken.</span>{' '}
        Neem contact op met uw winkel voor meer informatie.
      </p>
    </div>
  );
}

// ─── SchadeMeldenModal ────────────────────────────────────

function SchadeMeldenModal({ token, onClose }: { token: string; onClose: () => void }) {
  const submitSchade = useSubmitSchade(token);
  const [beschrijving, setBeschrijving] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    const combined = [...fotos, ...files].slice(0, 5);
    setFotos(combined);
    setPreviews(combined.map(f => URL.createObjectURL(f)));
    e.target.value = '';
  }

  function removeFoto(i: number) {
    const updated = fotos.filter((_, j) => j !== i);
    setFotos(updated);
    setPreviews(updated.map(f => URL.createObjectURL(f)));
  }

  function handleSubmit() {
    if (!beschrijving.trim()) return;
    submitSchade.mutate(
      { beschrijving: beschrijving.trim(), fotos },
      {
        onSuccess: () => setSuccess(true),
      },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-0 sm:px-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
        {success ? (
          <div className="text-center py-6">
            <ThumbsUp size={40} className="text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Schade gemeld</h3>
            <p className="text-sm text-gray-500 mb-4">
              Uw schademelding is ontvangen. Wij nemen zo snel mogelijk contact met u op.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-brand text-white rounded-xl text-sm font-semibold"
            >
              Sluiten
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Schade melden</h3>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Beschrijving <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={beschrijving}
                  onChange={e => setBeschrijving(e.target.value)}
                  placeholder="Beschrijf de schade zo nauwkeurig mogelijk..."
                  rows={4}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-all resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Foto's <span className="text-xs text-gray-400">(max. 5)</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFotoSelect}
                />
                {previews.length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-3">
                    {previews.map((src, i) => (
                      <div key={i} className="relative">
                        <img src={src} alt="" className="w-20 h-20 object-cover rounded-xl border border-gray-200" />
                        <button
                          onClick={() => removeFoto(i)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {fotos.length < 5 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-brand hover:text-brand transition-colors w-full justify-center"
                  >
                    <Camera size={16} /> Foto toevoegen
                  </button>
                )}
              </div>
            </div>

            {submitSchade.isError && (
              <p className="text-xs text-red-500 mt-3">Er ging iets mis. Probeer opnieuw.</p>
            )}

            <div className="flex gap-2 mt-5">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleSubmit}
                disabled={!beschrijving.trim() || submitSchade.isPending}
                className="flex-1 px-4 py-2.5 bg-brand text-white rounded-xl text-sm font-semibold hover:bg-[#b89a52] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {submitSchade.isPending ? 'Versturen...' : 'Melden'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── AllesInOrde ──────────────────────────────────────────

function AllesInOrde({ token }: { token: string }) {
  const submitFeedback = useSubmitFeedback(token);
  const [view, setView] = useState<'buttons' | 'feedback' | 'schade' | 'done'>('buttons');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [showSchadeModal, setShowSchadeModal] = useState(false);

  function handleFeedbackSubmit() {
    if (rating === 0) return;
    submitFeedback.mutate(
      { score: rating, comment: comment || undefined },
      { onSuccess: () => setView('done') },
    );
  }

  if (view === 'done') {
    return (
      <section className="bg-green-50 border border-green-200 rounded-2xl p-5 sm:p-6 text-center">
        <ThumbsUp size={32} className="text-green-500 mx-auto mb-2" />
        <p className="text-sm font-semibold text-green-800">Bedankt voor uw beoordeling!</p>
        <p className="text-xs text-green-600 mt-1">Wij waarderen uw feedback enorm.</p>
      </section>
    );
  }

  return (
    <>
      <section className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <ThumbsUp size={18} className="text-brand" />
          <h2 className="text-lg font-semibold text-gray-900">Hoe verliep de levering?</h2>
        </div>

        {view === 'buttons' && (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setView('feedback')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm font-medium hover:bg-green-100 transition-colors"
            >
              <Star size={16} className="text-green-600" /> Alles goed
            </button>
            <button
              onClick={() => setShowSchadeModal(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-100 transition-colors"
            >
              <Camera size={16} className="text-amber-600" /> Schade melden
            </button>
          </div>
        )}

        {view === 'feedback' && (
          <div className="space-y-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    size={32}
                    className={n <= (hoverRating || rating) ? 'text-brand fill-brand' : 'text-gray-300'}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Optionele opmerking..."
                  rows={3}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-all resize-none"
                />
                <button
                  onClick={handleFeedbackSubmit}
                  disabled={submitFeedback.isPending}
                  className="w-full py-2.5 bg-brand text-white rounded-xl text-sm font-semibold hover:bg-[#b89a52] disabled:opacity-40 transition-all"
                >
                  {submitFeedback.isPending ? 'Versturen...' : 'Beoordeling versturen'}
                </button>
              </>
            )}
            {submitFeedback.isError && (
              <p className="text-xs text-red-500 text-center">Er ging iets mis. Probeer opnieuw.</p>
            )}
            <button
              onClick={() => setView('buttons')}
              className="w-full text-xs text-gray-400 hover:text-gray-600 py-1"
            >
              Terug
            </button>
          </div>
        )}
      </section>

      {showSchadeModal && (
        <SchadeMeldenModal token={token} onClose={() => setShowSchadeModal(false)} />
      )}
    </>
  );
}

// ─── ActiviteitenLog ──────────────────────────────────────

function ActiviteitenLog({ token }: { token: string }) {
  const { data: activiteiten = [], isLoading } = usePubliekActiviteiten(token);

  if (isLoading || activiteiten.length === 0) return null;

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList size={18} className="text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Activiteiten</h2>
      </div>

      <div className="space-y-0">
        {activiteiten.map((act: ActiviteitPubliek, i: number) => {
          const isLast = i === activiteiten.length - 1;
          return (
            <div key={act.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-7 h-7 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-sm flex-shrink-0">
                  {act.icon || ''}
                </div>
                {!isLast && <div className="w-0.5 flex-1 bg-gray-100 my-1 min-h-[16px]" />}
              </div>
              <div className={`pb-4 flex-1 ${isLast ? 'pb-0' : ''}`}>
                <p className="text-sm text-gray-800">{act.tekst}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatDatumKort(act.tijdstip)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Mobile Tab Bar ────────────────────────────────────────

type Tab = 'overzicht' | 'berichten' | 'afspraken';

function MobileTabBar({ tab, setTab, berichtenCount, afsprakenCount }: {
  tab: Tab;
  setTab: (t: Tab) => void;
  berichtenCount: number;
  afsprakenCount: number;
}) {
  const tabs: Array<{ key: Tab; icon: React.ReactNode; label: string; badge?: number }> = [
    { key: 'overzicht', icon: <Home size={20} />, label: 'Overzicht' },
    { key: 'afspraken', icon: <CalendarDays size={20} />, label: 'Afspraken', badge: afsprakenCount },
    { key: 'berichten', icon: <MessageCircle size={20} />, label: 'Berichten', badge: berichtenCount },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 sm:hidden safe-bottom">
      <div className="flex">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-colors relative ${
              tab === t.key ? 'text-brand' : 'text-gray-400'
            }`}
          >
            {t.icon}
            {t.label}
            {t.badge !== undefined && t.badge > 0 && (
              <span className="absolute top-1.5 right-1/4 w-2 h-2 bg-brand rounded-full" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────

export function KlantportaalPage() {
  const { token } = useParams<{ token: string }>();
  const { data: dossier, isLoading, error } = usePubliekDossier(token || '');
  const { data: berichten = [] } = usePubliekBerichten(token || '');
  const { data: afspraken = [] } = usePubliekAfspraken(token || '');
  const { data: branding } = usePortaalBranding(token || '');
  const [tab, setTab] = useState<Tab>('overzicht');

  const openAfspraken = (afspraken as AfspraakPubliek[]).filter(a => a.status === 'voorstel').length;

  const accentColor = branding?.kleurPrimair || '#C8A961';

  // Fases voor chat tabs
  const chatFases = dossier?.trajectstappen.map(s => ({
    id: s.id,
    naam: parseNaam(s.template.naam),
    icon: s.template.icon,
  })) || [];

  const isVertraagd = !!(dossier?.leveringsdatum &&
    new Date(dossier.leveringsdatum) < new Date() &&
    dossier.status !== 'afgerond');

  const alleGeleverd = !!(dossier &&
    dossier.apparaten.length > 0 &&
    dossier.apparaten.every(a => a.status === 'GELEVERD'));

  const showFeedback = alleGeleverd || (dossier?.status === 'afgerond');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Dossier laden...</p>
        </div>
      </div>
    );
  }

  if (error || !dossier) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <AlertTriangle size={48} className="text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link ongeldig</h1>
          <p className="text-sm text-gray-500">
            Deze link is niet meer geldig of het dossier bestaat niet. Neem contact op met uw verkoper.
          </p>
        </div>
      </div>
    );
  }

  const statusLabel: Record<string, { tekst: string; kleur: string }> = {
    actief: { tekst: 'In uitvoering', kleur: 'bg-blue-100 text-blue-700' },
    afgerond: { tekst: 'Afgerond', kleur: 'bg-green-100 text-green-700' },
    geannuleerd: { tekst: 'Geannuleerd', kleur: 'bg-red-100 text-red-700' },
  };
  const s = statusLabel[dossier.status] || { tekst: dossier.status, kleur: 'bg-gray-100 text-gray-600' };

  return (
    <div className="min-h-screen bg-gray-50 pb-16 sm:pb-0">
      {/* Header */}
      <header className="bg-brand-secondary text-white">
        <div className="max-w-5xl mx-auto px-4 py-5 sm:py-6">
          <div className="flex items-center gap-3 mb-2">
            {branding?.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.bedrijfsNaam || 'Logo'} className="max-h-10 object-contain" />
            ) : (
              <span className="text-xl font-bold tracking-tight" style={{ color: accentColor }}>
                {branding?.bedrijfsNaam || 'Kordaro'}
              </span>
            )}
            <span className="text-white/30 text-xs">|</span>
            <span className="text-white/40 text-sm">Klantportaal</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-lg sm:text-xl font-semibold">{dossier.klantNaam}</h1>
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${s.kleur}`}>{s.tekst}</span>
          </div>
          <p className="text-sm text-white/50 mt-0.5">
            Dossier {dossier.dossierNummer} · {dossier.adres}, {dossier.postcode} {dossier.stad}
          </p>

          {/* Open afspraken notificatie */}
          {openAfspraken > 0 && (
            <div
              className="mt-3 flex items-center gap-2 rounded-xl px-4 py-2.5 cursor-pointer sm:cursor-default"
              style={{ backgroundColor: `${accentColor}20`, border: `1px solid ${accentColor}30` }}
              onClick={() => setTab('afspraken')}
            >
              <CalendarDays size={16} style={{ color: accentColor }} />
              <p className="text-sm font-medium" style={{ color: accentColor }}>
                {openAfspraken} afspraak{openAfspraken > 1 ? 'voorstellen' : 'voorstel'} wacht{openAfspraken === 1 ? '' : 'en'} op uw bevestiging
              </p>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5 sm:py-8">
        {/* Desktop: 3 kolommen */}
        <div className="hidden sm:grid sm:grid-cols-5 gap-6">
          {/* Links: overzicht */}
          <div className="col-span-3 space-y-5">
            {isVertraagd && <VertragingBanner leveringsdatum={dossier.leveringsdatum!} />}
            <DatumKaarten dossier={dossier} />
            <TrajectTimeline stappen={dossier.trajectstappen} />
            <AfsprakenLijst token={token || ''} />
            <ApparatenLijst apparaten={dossier.apparaten} />
            {showFeedback && <AllesInOrde token={token || ''} />}
            <DocumentenLijst documenten={dossier.documenten} token={token || ''} />
            <ActiviteitenLog token={token || ''} />

            {/* Winkel info */}
            <section className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Uw winkel</h3>
              <p className="text-sm font-medium text-gray-900">{dossier.entity.name}</p>
              <div className="flex gap-4 mt-2">
                {(branding?.telefoon || dossier.entity.phone) && (
                  <a href={`tel:${branding?.telefoon || dossier.entity.phone}`} className="text-sm text-brand hover:underline">
                    {branding?.telefoon || dossier.entity.phone}
                  </a>
                )}
                {(branding?.email || dossier.entity.email) && (
                  <a href={`mailto:${branding?.email || dossier.entity.email}`} className="text-sm text-brand hover:underline">
                    {branding?.email || dossier.entity.email}
                  </a>
                )}
              </div>
            </section>
          </div>

          {/* Rechts: chat */}
          <div className="col-span-2 sticky top-6 self-start">
            <KlantChat token={token || ''} klantNaam={dossier.klantNaam} fases={chatFases} />
          </div>
        </div>

        {/* Mobiel: tab-based */}
        <div className="sm:hidden space-y-4">
          {tab === 'overzicht' && (
            <>
              {isVertraagd && <VertragingBanner leveringsdatum={dossier.leveringsdatum!} />}
              <DatumKaarten dossier={dossier} />
              <TrajectTimeline stappen={dossier.trajectstappen} />
              <ApparatenLijst apparaten={dossier.apparaten} />
              {showFeedback && <AllesInOrde token={token || ''} />}
              <DocumentenLijst documenten={dossier.documenten} token={token || ''} />
              <ActiviteitenLog token={token || ''} />

              <section className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Uw winkel</h3>
                <p className="text-sm font-medium text-gray-900">{dossier.entity.name}</p>
                <div className="flex flex-col gap-2 mt-2">
                  {(branding?.telefoon || dossier.entity.phone) && (
                    <a href={`tel:${branding?.telefoon || dossier.entity.phone}`} className="text-sm text-brand hover:underline">
                      {branding?.telefoon || dossier.entity.phone}
                    </a>
                  )}
                  {(branding?.email || dossier.entity.email) && (
                    <a href={`mailto:${branding?.email || dossier.entity.email}`} className="text-sm text-brand hover:underline">
                      {branding?.email || dossier.entity.email}
                    </a>
                  )}
                </div>
              </section>
            </>
          )}

          {tab === 'afspraken' && (
            <AfsprakenLijst token={token || ''} />
          )}

          {tab === 'berichten' && (
            <KlantChat token={token || ''} klantNaam={dossier.klantNaam} fases={chatFases} />
          )}
        </div>
      </main>

      {/* Mobiel bottom tabs */}
      <MobileTabBar
        tab={tab}
        setTab={setTab}
        berichtenCount={berichten.length}
        afsprakenCount={openAfspraken}
      />

      {/* Footer */}
      <div className="text-center text-xs text-gray-400 py-4 space-y-1">
        <p>Laatst bijgewerkt: {formatDatum(dossier.updatedAt)}</p>
        {branding && (
          <p>
            Powered by{' '}
            <a
              href="https://c-bridge.app"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600 inline-flex items-center gap-0.5"
            >
              Kordaro <ExternalLink size={10} />
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
